import { createSelector } from 'reselect';

/**
 * Base selector to get Early Access page state
 * @param {Object} state - Redux state
 * @returns {Object} Early Access page state
 */
export const getEarlyAccessState = state => state.earlyAccessState;

/**
 * Selector to get the user's email for Early Access
 * @returns {string|null} User's email
 */
export const getEarlyAccessEmail = createSelector(
  [getEarlyAccessState],
  earlyAccessState => earlyAccessState?.email,
);

/**
 * Selector to check if the email is valid (matches email pattern)
 * @returns {boolean} Whether the email is valid
 */
export const getIsEarlyAccessEmailValid = createSelector(
  [getEarlyAccessEmail],
  email => {
    if (!email || email.length === 0) {
      return false;
    }
    // Validate email pattern: something@something.something
    const emailPattern = /^[^@]+@[^@]+\.[^@]+$/;
    return emailPattern.test(email);
  },
);

/**
 * Selector to check if Early Access request is loading
 * @returns {boolean} Whether request is loading
 */
export const getEarlyAccessIsLoading = createSelector(
  [getEarlyAccessState],
  earlyAccessState => earlyAccessState.loading,
);

/**
 * Selector to check if Early Access request has an error
 * @returns {boolean} Whether request has an error
 */
export const getEarlyAccessIsErrored = createSelector(
  [getEarlyAccessState],
  earlyAccessState => earlyAccessState.error,
);

/**
 * Selector to check if Early Access request was successful
 * @returns {boolean} Whether request was successful
 */
export const getEarlyAccessHasSucceeded = createSelector(
  [getEarlyAccessState],
  earlyAccessState => earlyAccessState.success,
);

/**
 * Selector to determine if error screen should be rendered
 * @returns {boolean} Whether to render error screen
 */
export const getEarlyAccessShouldRenderErrorScreen = createSelector(
  [getEarlyAccessIsLoading, getEarlyAccessIsErrored],
  (isLoading, isErrored) => {
    if (isLoading) {
      return false;
    }
    return isErrored;
  },
);

/**
 * Selector to determine if success screen should be rendered
 * @returns {boolean} Whether to render success screen
 */
export const getEarlyAccessShouldRenderSuccessScreen = createSelector(
  [getEarlyAccessIsLoading, getEarlyAccessHasSucceeded],
  (isLoading, hasSucceeded) => {
    if (isLoading) {
      return false;
    }
    return hasSucceeded;
  },
);

/**
 * Selector to determine if the form should be rendered
 * @returns {boolean} Whether to render the form
 */
export const getEarlyAccessShouldRenderForm = createSelector(
  [
    getEarlyAccessIsLoading,
    getEarlyAccessHasSucceeded,
    getEarlyAccessIsErrored,
  ],
  (isLoading, hasSucceeded, isErrored) => {
    if (isLoading) {
      return false;
    }
    return !hasSucceeded && !isErrored;
  },
);

/**
 * Selector to determine if the submit button should be disabled
 * @returns {boolean} Whether submit is disabled
 */
export const getIsEarlyAccessSubmitDisabled = createSelector(
  [getIsEarlyAccessEmailValid, getEarlyAccessIsLoading],
  (isEmailValid, isLoading) => {
    return !isEmailValid || isLoading;
  },
);

/**
 * Selector to get the current step in the Early Access flow
 * @returns {number} The current step
 */
export const getEarlyAccessStep = createSelector(
  [getEarlyAccessState],
  earlyAccessState => earlyAccessState.earlyAccessStep,
);

/**
 * Selector to get the current step in the Early Access create user flow
 * @returns {number} The current step
 */
export const getEarlyAccessCreateUserStep = createSelector(
  [getEarlyAccessState],
  earlyAccessState => earlyAccessState.earlyAccessCreateUserStep,
);
